<template>
  <div class="sp-container" :class="modifierClasses">
    <slot class="slot" />
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useBreakpoints } from "../../composables/breakpoints";
import { toCssUnit } from "../../utils/props";

const props = defineProps({
  flex: {
    type: [Boolean, String],
    default: false,
  },
  flexWrap: {
    type: String,
    validator: (value) => ["wrap", "nowrap"].includes(value),
    default: "wrap",
  },

  rowgap: {
    type: [String, Number],
    default: "0",
  },

  columngap: {
    type: [String, Number],
    default: "0",
  },

  direction: {
    type: String,
    default: "row",
    validator: (value) => ["row", "column"].includes(value),
  },

  directionSm: {
    type: String,
    default: "column",
    validator: (value) => ["row", "column"].includes(value),
  },
  alignItems: {
    type: String,
    default: "normal",
    validator: (value) => ["normal", "start", "end", "center", "stretch"].includes(value),
  },
  justifyContent: {
    type: String,
    default: "normal",
    validator: (value) => ["normal", "start", "end", "center", "stretch"].includes(value),
  },
});

const { isSmScreen } = useBreakpoints();
const modifierClasses = computed(() => ({
  "--flex": props.flex,
  [`--direction-${props.direction}`]: props.direction,
  [`--direction-sm-${props.directionSm}`]: props.directionSm,
  "--is-sm-screen": isSmScreen.value,
}));

const rowGap = computed(() => toCssUnit(props.rowgap));
const columnGap = computed(() => toCssUnit(props.columngap));
</script>

<style>
:host {
  display: block;
  width: 100%;
}
</style>

<style scoped lang="scss">
.sp-container {
  &.--flex {
    --direction: v-bind(direction);
    --row-gap: v-bind(rowGap);
    --column-gap: v-bind(columnGap);
    --align-items-prop: v-bind(alignItems);
    --align-items: var(--sp-comp-container-align-items, var(--align-items-prop));
    --justify-content-prop: v-bind(justifyContent);
    --justify-content: var(--sp-comp-container-justify-content, var(--justify-content-prop));

    .slot {
      display: flex;
      flex-direction: var(--direction);
      width: 100%;
      row-gap: var(--row-gap);
      column-gap: var(--column-gap);
      flex-wrap: v-bind(flexWrap);
      align-items: var(--align-items);
      justify-content: var(--justify-content);
    }

    &.--direction-column,
    &.--is-sm-screen.--direction-sm-column {
      ::slotted(*) {
        width: 100%;
      }
    }

    &.--is-sm-screen {
      --direction: v-bind(directionSm);
    }
  }
}
</style>
